import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, X } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DireccionCursos() {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModulosModal, setShowModulosModal] = useState(false);
    const [selectedCurso, setSelectedCurso] = useState(null);

    // Timezone-safe date formatter: extracts YYYY-MM-DD to avoid UTC shift
    const formatSafeDate = (dateStr) => {
        if (!dateStr) return '-';
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            const d = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            return d.toLocaleDateString();
        }
        return new Date(dateStr).toLocaleDateString();
    };

    useEffect(() => {
        loadCursos();
    }, []);

    const loadCursos = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/direccion/autorizaciones/cursos`);
            const data = await response.json();
            setCursos(data.cursos || []);
        } catch (error) {
            console.error('Error:', error);
            setCursos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEstadoChange = async (id, estado) => {
        try {
            await fetch(`${API_URL}/direccion/autorizaciones/cursos/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado })
            });
            loadCursos();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const exportToExcel = () => {
        try {
            if (!filteredCursos || filteredCursos.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            const rows = [];
            let counter = 1;

            filteredCursos.forEach((c) => {
                // Parse modulos
                let modulos = c.modulos;
                if (typeof modulos === 'string') {
                    try { modulos = JSON.parse(modulos); } catch (e) { modulos = []; }
                }
                if (!Array.isArray(modulos)) modulos = [];

                const courseBaseInfo = {
                    'No': counter++,
                    'Nombre Curso': c.nombre || '',
                    'Descripción': c.descripcion || c.modalidad || '',
                    'Costo (Bs.)': c.costo || '',
                    'Cuotas': c.numero_cuotas || '',
                    'Capacidad': c.capacidad || '-',
                    'Observaciones': c.observaciones || '-',
                    'Estado': getEstadoLabel(c.estado)
                };

                if (modulos.length === 0) {
                    rows.push({
                        ...courseBaseInfo,
                        'Módulo': '-',
                        'Facilitador': '-',
                        'Fecha Inicio': '-',
                        'Fecha Fin': '-',
                        'Hora Inicio': '-',
                        'Hora Fin': '-',
                        'Días': '-',
                        'Duración': '-',
                        'Carga Horaria': '-',
                        'Sesiones': '-',
                        'Sala': '-'
                    });
                } else {
                    modulos.forEach((m, idx) => {
                        rows.push({
                            ...courseBaseInfo,
                            'Módulo': m.nombre || `Módulo ${idx + 1}`,
                            'Facilitador': m.facilitador || '-',
                            'Fecha Inicio': formatSafeDate(m.fecha_inicio),
                            'Fecha Fin': formatSafeDate(m.fecha_fin),
                            'Hora Inicio': m.hora_inicio || '-',
                            'Hora Fin': m.hora_fin || '-',
                            'Días': formatDias(m.dias),
                            'Duración': m.duracion || '-',
                            'Carga Horaria': m.carga_horaria || '-',
                            'Sesiones': m.sesiones || '-',
                            'Sala': m.sala || '-'
                        });
                    });
                }
            });

            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Cursos');
            XLSX.writeFile(wb, `cursos_extension_direccion_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
            alert('Error al generar el archivo Excel.');
        }
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'pendiente_aprobacion': return 'bg-yellow-200 text-yellow-800';
            case 'aprobado': return 'bg-green-200 text-green-800';
            case 'disponible': return 'bg-green-200 text-green-800';
            case 'rechazado': return 'bg-red-200 text-red-800';
            default: return 'bg-gray-200 text-gray-700';
        }
    };

    const getEstadoLabel = (estado) => {
        switch (estado) {
            case 'pendiente_aprobacion': return 'Pendiente Aprobación';
            case 'aprobado': return 'Aprobado';
            case 'disponible': return 'Aprobado';
            case 'rechazado': return 'Rechazado';
            default: return estado || 'Pendiente';
        }
    };

    const handleViewModulos = (curso) => {
        let parsedModulos = curso.modulos;
        if (typeof parsedModulos === 'string') {
            try { parsedModulos = JSON.parse(parsedModulos); } catch (e) { parsedModulos = []; }
        }
        setSelectedCurso({ ...curso, modulos: parsedModulos });
        setShowModulosModal(true);
    };

    // Format days as abbreviations
    const formatDias = (dias) => {
        if (!dias) return '-';
        const diasMap = {
            'lunes': 'L',
            'martes': 'M',
            'miercoles': 'Mi',
            'jueves': 'J',
            'viernes': 'V'
        };
        return dias.split(',').map(d => diasMap[d.trim()] || d.trim()).join(', ');
    };

    const handleDeadlineChange = async (id, fecha_limite_inscripcion) => {
        try {
            await fetch(`${API_URL}/direccion/autorizaciones/cursos/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fecha_limite_inscripcion })
            });
            loadCursos();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const filteredCursos = cursos.filter(c =>
        searchTerm === '' || c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Dirección Académica &gt; Cursos de Extensión</h1>
                <p className="text-sm text-gray-600 mt-1">Autorización y Aprobación de Cursos</p>
            </div>

            {/* <div className="mb-4 flex flex-wrap gap-3">
                <select
                    value={new Date().getFullYear()}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm"
                    disabled // Deshabilitado porque Jefatura filtra en frontend pero aca dejamos la UI consistente
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>

                <div className="flex-1 max-w-md relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full" />
                </div>
                <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                    <Download size={20} />
                </button>
            </div> */}

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={new Date().getFullYear()}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                                <option key={g} value={g}>Gestión {g}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                    <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm" title="Descargar Excel">
                        <Download size={20} />
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nombre Curso</th>
                                <th className="px-3 py-3 text-center font-semibold border">Descripción</th>
                                <th className="px-3 py-3 text-center font-semibold border">Costo (Bs.)</th>
                                <th className="px-3 py-3 text-center font-semibold border">Cuotas</th>
                                <th className="px-3 py-3 text-center font-semibold border">Capacidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Fecha Límite</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredCursos.length === 0 ? (
                                <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No hay cursos registrados</td></tr>
                            ) : (
                                filteredCursos.map((c, index) => (
                                    <tr key={c.id_curso} className="hover:bg-gray-50">
                                        <td className="px-3 py-3 text-center border">{index + 1}</td>
                                        <td className="px-3 py-3 font-medium border">{c.nombre || '-'}</td>
                                        <td className="px-3 py-3 max-w-xs truncate border">{c.descripcion || '-'}</td>
                                        <td className="px-3 py-3 border">{c.costo ? `Bs. ${parseFloat(c.costo).toFixed(2)}` : '-'}</td>
                                        <td className="px-3 py-3 border">{c.numero_cuotas || '-'}</td>
                                        <td className="px-3 py-3 font-semibold text-blue-700 border">{c.capacidad || '30'}</td>
                                        <td className="px-3 py-3 border">{c.observaciones || '-'}</td>
                                        <td className="px-3 py-3 text-center border">
                                            <input
                                                type="date"
                                                value={c.fecha_limite_inscripcion ? c.fecha_limite_inscripcion.split('T')[0] : ''}
                                                onChange={(e) => handleDeadlineChange(c.id_curso, e.target.value)}
                                                className="px-2 py-1 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </td>
                                        <td className="px-3 py-3 text-center border">
                                            <select
                                                value={c.estado || 'pendiente_aprobacion'}
                                                onChange={(e) => handleEstadoChange(c.id_curso, e.target.value)}
                                                className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getEstadoColor(c.estado || 'pendiente_aprobacion')}`}
                                            >
                                                <option value="pendiente_aprobacion">Pendiente</option>
                                                <option value="disponible">Aprobado</option>
                                                <option value="rechazado">Rechazado</option>
                                            </select>
                                        </td>
                                        <td className="px-3 py-3 border">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleViewModulos(c)} className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800" title="Ver Detalles">
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Ver Módulos */}
            {showModulosModal && selectedCurso && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">Detalles del Curso</h2>
                            <button onClick={() => { setShowModulosModal(false); setSelectedCurso(null); }} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Información General del Curso */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-blue-800 mb-4">Información del Curso</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div>
                                        <span className="text-sm text-gray-500">Nombre Curso</span>
                                        <p className="font-medium">{selectedCurso.nombre || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Descripción</span>
                                        <p className="font-medium">{selectedCurso.descripcion || selectedCurso.modalidad || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Precio Curso</span>
                                        <p className="font-medium">Bs. {selectedCurso.costo || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Número de Cuotas</span>
                                        <p className="font-medium">{selectedCurso.numero_cuotas || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Capacidad</span>
                                        <p className="font-bold text-blue-700">{selectedCurso.capacidad || '30'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Observaciones</span>
                                        <p className="font-medium">{selectedCurso.observaciones || '-'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Estado</span>
                                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${getEstadoColor(selectedCurso.estado)}`}>
                                            {getEstadoLabel(selectedCurso.estado)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Módulos del Curso */}
                            <div>
                                <h3 className="text-lg font-semibold text-blue-800 mb-4">Módulos del Curso</h3>
                                <div className="bg-white rounded-lg shadow overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-100 border-b">
                                                <tr>
                                                    <th className="px-3 py-3 text-center font-semibold border">Módulo</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Facilitador</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Fecha Inicio</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Fecha Fin</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Hora Inicio</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Hora Fin</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Días</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Duración</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Carga Horaria</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Sesiones</th>
                                                    <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {selectedCurso.modulos && selectedCurso.modulos.length > 0 ? (
                                                    selectedCurso.modulos.map((modulo, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-3 py-3 font-medium border">{modulo.nombre || `Módulo ${idx + 1}`}</td>
                                                            <td className="px-3 py-3 border">{modulo.facilitador || '-'}</td>
                                                            <td className="px-3 py-3 border">{formatSafeDate(modulo.fecha_inicio)}</td>
                                                            <td className="px-3 py-3 border">{formatSafeDate(modulo.fecha_fin)}</td>
                                                            <td className="px-3 py-3 border">{modulo.hora_inicio || '-'}</td>
                                                            <td className="px-3 py-3 border">{modulo.hora_fin || '-'}</td>
                                                            <td className="px-3 py-3 text-center">{formatDias(modulo.dias)}</td>
                                                            <td className="px-3 py-3 border">{modulo.duracion || '-'}</td>
                                                            <td className="px-3 py-3 border">{modulo.carga_horaria || '-'}</td>
                                                            <td className="px-3 py-3 border">{modulo.sesiones || '-'}</td>
                                                            <td className="px-3 py-3 border">{modulo.sala || '-'}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                                                            No hay módulos registrados para este curso
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Botón Cerrar */}
                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => { setShowModulosModal(false); setSelectedCurso(null); }}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                                >Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
